import { useEffect, useMemo, useState } from 'react';

import { SocialFeedEntry } from '@/types/socialFeed.generated';
import { formatDistanceToNowStrict } from 'date-fns';

/**
 * Formats a date into a “time ago” label
 * @param dateString - The date string to calculate the time ago from
 * @returns A formatted time ago string (e.g., "5m ago", "2h ago", "3d ago")
 */
export function timeAgo(dateString: string) {
    return formatDistanceToNowStrict(dateString, {
        addSuffix: true,
    })
        .replace(' minutes', 'm')
        .replace(' minute', 'm')
        .replace(' hours', 'h')
        .replace(' hour', 'h')
        .replace(' days', 'd')
        .replace(' day', 'd')
        .replace(' months', 'mo')
        .replace(' month', 'mo')
        .replace(' years', 'y')
        .replace(' year', 'y');
}

export const getFirstStartingEvent = (picks?: SocialFeedEntry['picks']) => {
    if (!picks || picks.length === 0) {
        return null;
    }
    const sortedPicks = [...picks].sort((a, b) => {
        const dateA = new Date(a.eventDate).getTime();
        const dateB = new Date(b.eventDate).getTime();
        return dateA - dateB;
    });
    return sortedPicks[0];
};

export function formatCountdown(ms: number) {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);

    if (hours > 24) {
        const days = Math.floor(hours / 24);
        return `${days} ${days === 1 ? 'day' : 'days'}`;
    }

    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = (n: number) => n.toString().padStart(2, '0');

    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export function useCurrentTime(enabled: boolean) {
    const [currentTime, setCurrentTime] = useState(Date.now());

    useEffect(() => {
        let interval: ReturnType<typeof setInterval>;
        if (enabled) {
            interval = setInterval(() => {
                setCurrentTime(Date.now());
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [enabled]);

    return currentTime;
}

/**
 *  Returns a current-time reference that updates only when the next event starts
 */
export function useNextEventTime(socialFeed?: SocialFeedEntry[]) {
    const [eventStartTime, setEventStartTime] = useState(() => Date.now());

    const nextEventStartTime = useMemo(() => {
        if (!socialFeed) {
            return undefined;
        }
        return socialFeed
            .map(entry => getFirstStartingEvent(entry.picks))
            .filter(event => !!event)
            .map(event => new Date(event.eventDate).getTime())
            .filter(time => time > eventStartTime)
            .sort((a, b) => a - b)[0];
    }, [socialFeed, eventStartTime]);

    useEffect(() => {
        if (!nextEventStartTime) {
            return;
        }

        const delay = Math.max(0, nextEventStartTime - Date.now());
        const timeout = setTimeout(() => setEventStartTime(Date.now()), delay);

        return () => clearTimeout(timeout);
    }, [nextEventStartTime]);

    return eventStartTime;
}

export function useEventCountdown(currentTime: number, eventDate?: string) {
    const [timeLeft, setTimeLeft] = useState('00:00:00');

    useEffect(() => {
        if (!eventDate) {
            setTimeLeft('00:00:00');
            return;
        }
        const target = new Date(eventDate).getTime();
        const diff = target - currentTime;

        if (diff <= 0) {
            setTimeLeft('00:00:00');
            return;
        }

        setTimeLeft(formatCountdown(diff));
    }, [eventDate, currentTime]);

    return timeLeft;
}
